import React from 'react';
import { isVisibleAtScale } from '@agissc/ktag-map';
import { EsriLayerSettingsProps } from '../mapContent/topics/esrilayersettings/EsriLayerSettings';
import { LegendProps, MapLayerSetting } from '../mapContent/api/Contracts';
import useAppState from '../../AppStore';
import List from '../../components/list/List';
import LegendSymbols from './LegendSymbols';
import { isVisibleUpToRoot } from '../mapContent/api/mapUtilities';
import { useLegendQuery } from '../mapContent/api/useLegendQuery';
import { useMetadataQuery } from '../mapContent/api/useMetadataQuery';
import NoLayerLegend from './NoLayerLegend';
import useSubLayersSetting from '../mapContent/api/useSubLayersSetting';
import LegendItemPlaceholder from './LegendItemPlaceholder';

interface LegendItemProps {
  ids: Array<number>;
  subLayersSetting: MapLayerSetting[];
  resolution: number | undefined;
  legend: Record<number, LegendProps[]>;
}

const LegendList = ({ ids, subLayersSetting, resolution, legend }: LegendItemProps) => {
  const items = subLayersSetting.filter((layer) => ids.includes(layer.id)) || [];

  const hasVisibileChild = (currentId: number): boolean => {
    const subLayers = subLayersSetting?.filter(
      (element) => element.parentLayerId === currentId,
    );

    return (
      !subLayers?.length ||
      subLayers.some(
        (child) =>
          isVisibleAtScale(resolution, child.minScale, child.maxScale) &&
          hasVisibileChild(child.id),
      )
    );
  };

  return (
    <List
      liClassName="legend-list__item"
      sortBy="order"
      items={items}
      renderItem={(item) =>
        isVisibleAtScale(resolution, item.minScale, item.maxScale) &&
        hasVisibileChild(item.id) && (
          <>
            {item.subLayerIds && (
              <LegendList
                ids={item.subLayerIds}
                subLayersSetting={subLayersSetting}
                resolution={resolution}
                legend={legend}
              />
            )}
            {legend[item.id] && (
              <LegendSymbols
                legend={legend[item.id]}
                title={item.name}
                isRoot={item.parentLayerId === -1}
              />
            )}
          </>
        )
      }
    />
  );
};

const LayerLegend = ({ topicId, url, isPublic = true }: EsriLayerSettingsProps) => {
  const location = useAppState((state) => state.location);
  const legendQuery = useLegendQuery(url, isPublic);
  const layersConfigQuery = useMetadataQuery(topicId);
  const mapLayerSetting = useSubLayersSetting(topicId, url, isPublic);

  if (legendQuery.isLoading || layersConfigQuery.isLoading || mapLayerSetting.isLoading)
    return <LegendItemPlaceholder />;

  const legend = legendQuery.data ?? {};
  const layersConfig = layersConfigQuery.data ?? {};
  const layerSetting = mapLayerSetting.data ?? {};

  const subLayersSetting = Object.values(layerSetting).filter(
    (layer) =>
      layer.isVisible && (layer.isGroupLayer || layersConfig[layer.id]?.displayInLegend),
  );

  const anyLayerIsVisible = subLayersSetting.some((item) =>
    isVisibleUpToRoot(item.id, subLayersSetting, location.resolution),
  );

  if (!anyLayerIsVisible) {
    return <NoLayerLegend topicId={topicId} />;
  }

  const rootLayerIds = subLayersSetting
    .filter((item) => item.isRoot)
    .map((item) => item.id);

  return (
    <LegendList
      ids={rootLayerIds}
      subLayersSetting={subLayersSetting}
      resolution={location.resolution}
      legend={legend}
    />
  );
};

export default LayerLegend;
